import React, { useState, useEffect, useCallback, useMemo } from 'react';
import useFocusRefetch from '../hooks/useFocusRefetch';
import { getCurrentSession } from '../services/authService';
import { useToast } from '../ui/primitives';
import TripEditModal from './TripEditModal';
import './OpsCalendar.css';

const LIVE_OR_DONE = ['EN_ROUTE', 'PICKED_UP', 'DROPPED_OFF', 'COMPLETED', 'CANCELLED'];

const API_BASE = process.env.REACT_APP_MOVORRA_API_URL || 'http://localhost:8081';
const DAYS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

function fmtTime(h) {
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  const ampm = hh >= 12 ? 'p' : 'a';
  const disp = ((hh + 11) % 12) + 1;
  return mm === 0 ? `${disp}${ampm}` : `${disp}:${String(mm).padStart(2,'0')}${ampm}`;
}

function shortLoc(loc) {
  if (!loc) return '-';
  if (loc.airportCode) return loc.airportCode;
  return loc.city || loc.addressLine1 || '-';
}

function fullLoc(loc) {
  if (!loc) return '';
  return [loc.addressLine1, loc.city, loc.state, loc.airportCode].filter(Boolean).join(' ');
}

// Lowercased renderings of a pickup date so search matches common typing:
// "sun, jun 28, 2026", "jun 28", "6/28/2026", "6/28", "2026-06-28".
function dateSearchText(dt) {
  if (!dt) return '';
  const d = new Date(dt);
  if (isNaN(d.getTime())) return '';
  const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return [
    d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }),
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`,
    `${d.getMonth() + 1}/${d.getDate()}`,
    iso,
  ].join(' ').toLowerCase();
}

const TONES = {
  SCHEDULED:  { bg: '#E6ECF2', bar: '#0B2540', text: '#05111F' },
  CONFIRMED:  { bg: '#F0FDF4', bar: '#16A34A', text: '#14532D' },
  ASSIGNED:   { bg: '#E6ECF2', bar: '#0B2540', text: '#05111F' },
  EN_ROUTE:   { bg: '#EFF6FF', bar: '#2563EB', text: '#1E3A5F' },
  PICKED_UP:  { bg: '#EFF6FF', bar: '#2563EB', text: '#1E3A5F' },
  DROPPED_OFF:{ bg: '#F0FDF4', bar: '#16A34A', text: '#14532D' },
  COMPLETED:  { bg: '#F2F4F7', bar: '#9CA3AF', text: '#374151' },
  CANCELLED:  { bg: '#FEF2F2', bar: '#DC2626', text: '#7F1D1D' },
};

// Mirrors the dispatch board's vehicle label (ride type · vehicle).
function vehicleLabel(bt, rideName) {
  if (!bt) return null;
  const cls = rideName || (bt.tripType ? bt.tripType.charAt(0) + bt.tripType.slice(1).toLowerCase() : null);
  const veh = bt.vehicleName || null;
  if (cls && veh && cls !== veh) return `${cls} · ${veh}`;
  return cls || veh || null;
}

// Free-text match for an event (same fields the board search uses).
function eventMatches(e, q) {
  if (!q) return true;
  return [
    e.bookingNumber, e.customerName,
    fullLoc(e.trip.pickup), fullLoc(e.trip.dropoff),
    dateSearchText(e.trip.pickup?.datetime),
  ].filter(Boolean).join(' ').toLowerCase().includes(q);
}

function OpsCalendar({ authSession }) {
  const [bookings, setBookings] = useState([]);
  const [, setVehicles] = useState([]); // vehicles fetched but not yet rendered
  const [trips, setTrips] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [rideTypes, setRideTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editEvent, setEditEvent] = useState(null);
  const toast = useToast();
  const [weekStart, setWeekStart] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - d.getDay() + 1); d.setHours(0,0,0,0); return d;
  });
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState('week');

  const fetchAll = useCallback(async ({ silent } = {}) => {
    if (!silent) setLoading(true);
    try {
      let token = authSession?.idToken;
      try { const s = await getCurrentSession(); token = s.idToken; } catch (e) {}
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      // Month view spans the whole month grid, so load the month (± a week for spillover
      // cells); week/day load the tighter week window.
      let from, to;
      if (view === 'month') {
        from = new Date(weekStart.getFullYear(), weekStart.getMonth(), 1); from.setDate(from.getDate() - 7);
        to = new Date(weekStart.getFullYear(), weekStart.getMonth() + 1, 0); to.setDate(to.getDate() + 7);
      } else {
        from = new Date(weekStart); from.setDate(from.getDate() - 1);
        to = new Date(weekStart); to.setDate(to.getDate() + 8);
      }

      const fromStr = from.toISOString().replace('Z','');
      const toStr = to.toISOString().replace('Z','');
      const [bkRes, vehRes, tripRes, drvRes, custRes, rtRes] = await Promise.all([
        fetch(`${API_BASE}/api/bookings/with-trips/range?from=${fromStr}&to=${toStr}`, { headers }),
        fetch(`${API_BASE}/api/vehicles`, { headers }),
        fetch(`${API_BASE}/api/trips/range?from=${fromStr}&to=${toStr}`, { headers }),
        fetch(`${API_BASE}/api/drivers`, { headers }),
        fetch(`${API_BASE}/api/customers`, { headers }),
        fetch(`${API_BASE}/api/ride-types`, { headers }),
      ]);

      if (bkRes.ok) setBookings(await bkRes.json());
      if (vehRes.ok) setVehicles(await vehRes.json());
      if (tripRes.ok) setTrips(await tripRes.json());
      if (drvRes.ok) setDrivers(await drvRes.json());
      if (custRes.ok) setCustomers(await custRes.json());
      if (rtRes.ok) setRideTypes(await rtRes.json());
    } catch (err) {
      console.error('Calendar fetch error:', err);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [authSession, weekStart, view]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  const refresh = useFocusRefetch(fetchAll);

  // Background poll (~12s, mirrors the dispatch board) so trip-status changes — e.g. a
  // driver completing a trip — show on the calendar without a manual refresh.
  useEffect(() => {
    const id = setInterval(() => fetchAll({ silent: true }), 12000);
    return () => clearInterval(id);
  }, [fetchAll]);

  const driverMap = useMemo(() => { const m = {}; drivers.forEach(d => { m[d.driverId] = d; }); return m; }, [drivers]);
  const tripMap = useMemo(() => { const m = {}; trips.forEach(t => { m[t.tripId] = t; }); return m; }, [trips]);
  const customerMap = useMemo(() => {
    const m = {}; customers.forEach(c => { m[c.customerId] = `${c.firstName || ''} ${c.lastName || ''}`.trim(); }); return m;
  }, [customers]);
  const rideTypeMap = useMemo(() => { const m = {}; rideTypes.forEach(rt => { m[rt.rideTypeId] = rt.rideName; }); return m; }, [rideTypes]);

  // Single enriched event shape used by every view (week/day/month) so they're consistent
  // and all clickable. Mirrors the dispatch board's per-row info: customer, route, driver, vehicle.
  const buildEvent = useCallback((b, trip) => {
    const linkedTrip = trip.tripId ? tripMap[trip.tripId] : null;
    const driverId = linkedTrip?.driverId;
    const driver = driverId ? driverMap[driverId] : null;
    const dt = trip.pickup?.datetime ? new Date(trip.pickup.datetime) : null;
    const startHour = dt ? dt.getHours() + dt.getMinutes() / 60 : 0;
    const duration = trip.distanceMiles ? Math.max(1, Number(trip.distanceMiles) / 30) : 2;
    return {
      id: `${b.bookingId}-${trip.bookingTripId}`,
      bookingId: b.bookingId,
      bookingNumber: b.bookingNumber,
      customerId: b.customerId,
      customerName: customerMap[b.customerId] || '',
      trip,
      linkedTrip,
      driverId,
      driver,
      status: trip.tripStatus || 'SCHEDULED',
      label: `${shortLoc(trip.pickup)} → ${shortLoc(trip.dropoff)}`,
      pax: trip.travelers?.numTravelers || 0,
      vehicle: vehicleLabel(trip, rideTypeMap[trip.rideTypeId]),
      start: startHour,
      end: Math.min(24, startHour + duration),
    };
  }, [tripMap, driverMap, customerMap, rideTypeMap]);

  const getHeaders = useCallback(async () => {
    let token = authSession?.idToken;
    try { const s = await getCurrentSession(); token = s.idToken; } catch (e) {}
    const h = { 'Content-Type': 'application/json' };
    if (token) h['Authorization'] = `Bearer ${token}`;
    return h;
  }, [authSession]);

  // Driver (re)assignment for the edit modal — mirrors the dispatch board's trip-path:
  // assign on the linked Trip, or spin one up from the booking trip if none exists yet.
  const assignDriver = useCallback(async (row, d) => {
    const headers = await getHeaders();
    if (row.linkedTrip?.tripId) {
      await fetch(`${API_BASE}/api/trips/${row.linkedTrip.tripId}/assign-driver/${d.driverId}`, { method: 'PATCH', headers });
    } else {
      await fetch(`${API_BASE}/api/trips/from-booking-trip/${row.trip.bookingTripId}?driverId=${d.driverId}`, { method: 'POST', headers });
    }
  }, [getHeaders]);
  const unassignDriver = useCallback(async (row) => {
    if (!row.linkedTrip?.tripId) return;
    const headers = await getHeaders();
    await fetch(`${API_BASE}/api/trips/${row.linkedTrip.tripId}/unassign-driver`, { method: 'PATCH', headers });
  }, [getHeaders]);

  // Week-grid events (carry day + start/end on top of the shared shape).
  const events = useMemo(() => {
    const evts = [];
    bookings.forEach(b => {
      (b.trips || []).forEach(trip => {
        if (!trip.pickup?.datetime) return;
        const dt = new Date(trip.pickup.datetime);
        const dayOfWeek = (dt.getDay() + 6) % 7; // Mon=0
        const startHour = dt.getHours() + dt.getMinutes() / 60;
        const duration = trip.distanceMiles ? Math.max(1, Number(trip.distanceMiles) / 30) : 2; // estimate
        evts.push({ ...buildEvent(b, trip), day: dayOfWeek, start: startHour, end: Math.min(24, startHour + duration) });
      });
    });
    return evts;
  }, [bookings, buildEvent]);

  // Free-text search over what's loaded: customer name, booking #, full pickup/dropoff
  // address (street + city + state + airport), or the pickup date.
  const filteredEvents = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return events;
    return events.filter(e => eventMatches(e, q));
  }, [events, search]);

  const shift = (dir) => {
    const d = new Date(weekStart);
    if (view === 'month') d.setMonth(d.getMonth() + dir);
    else if (view === 'day') d.setDate(d.getDate() + dir);
    else d.setDate(d.getDate() + dir * 7);
    setWeekStart(d);
  };

  const goToday = () => {
    const d = new Date(); d.setHours(0,0,0,0);
    if (view === 'week') d.setDate(d.getDate() - d.getDay() + 1); // week anchors on Monday; day/month on today
    setWeekStart(d);
  };

  // Re-anchor weekStart when switching views so each view shows a sensible date:
  // day → today, week → Monday of the current week, month → keep (its month is what matters).
  const switchView = (v) => {
    if (v === 'day') { const t = new Date(); t.setHours(0,0,0,0); setWeekStart(t); }
    else if (v === 'week') { const d = new Date(weekStart); d.setDate(d.getDate() - d.getDay() + 1); d.setHours(0,0,0,0); setWeekStart(d); }
    setView(v);
  };

  // Jump straight to a specific day (from a Week header / Month cell click).
  const goToDay = (date) => {
    const d = new Date(date); d.setHours(0,0,0,0);
    setWeekStart(d); setView('day');
  };

  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);

  const headerLabel = view === 'month'
    ? weekStart.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : view === 'day'
    ? weekStart.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' })
    : `${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

  const todayIndex = (() => {
    const now = new Date();
    const diff = Math.floor((now - weekStart) / 86400000);
    return diff >= 0 && diff < 7 ? diff : -1;
  })();

  // Group events by day for the grid
  const eventsByDay = useMemo(() => {
    const byDay = Array.from({ length: 7 }, () => []);
    filteredEvents.forEach(e => { if (e.day >= 0 && e.day < 7) byDay[e.day].push(e); });
    return byDay;
  }, [filteredEvents]);

  if (loading) return <div className="cal-loading">Loading calendar...</div>;

  return (
    <div className="cal-root">
      {/* Toolbar */}
      <div className="cal-toolbar">
        <div className="cal-tool-left">
          <button className="cal-today-btn" onClick={goToday}>Today</button>
          <div className="cal-nav-group">
            <button className="cal-nav-btn" onClick={() => shift(-1)}>‹</button>
            <button className="cal-nav-btn" onClick={() => shift(1)}>›</button>
          </div>
          <span className="cal-week-label">{headerLabel}</span>
        </div>
        <div className="cal-tool-right">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, booking #, city…"
            aria-label="Search trips"
            style={{ marginRight: 10, padding: '6px 10px', borderRadius: 8, border: '1px solid #e6e8ec', fontSize: 13, width: 210 }}
          />
          <button type="button" onClick={refresh} title="Refresh data" style={{ marginRight: 8 }}>↻ Refresh</button>
          <span className="cal-trip-count">{filteredEvents.length} trips</span>
          <div className="cal-view-toggle">
            {['day', 'week', 'month'].map(v => (
              <button key={v} className={`cal-view-btn ${view === v ? 'active' : ''}`} onClick={() => switchView(v)}>
                {v.charAt(0).toUpperCase() + v.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Month view */}
      {view === 'month' && (() => {
        const year = weekStart.getFullYear();
        const month = weekStart.getMonth();
        const firstDay = new Date(year, month, 1);
        const startOffset = (firstDay.getDay() + 6) % 7;
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const cells = [];
        for (let i = 0; i < startOffset; i++) cells.push(null);
        for (let d = 1; d <= daysInMonth; d++) cells.push(d);
        while (cells.length % 7 !== 0) cells.push(null);

        const today = new Date();
        const isThisMonth = today.getFullYear() === year && today.getMonth() === month;

        return (
          <div className="cal-month-wrap">
            <div className="cal-month-header">
              {DAYS.map(d => <div key={d} className="cal-month-day-head">{d}</div>)}
            </div>
            <div className="cal-month-grid">
              {cells.map((day, i) => {
                if (day === null) return <div key={i} className="cal-month-cell empty" />;
                const isToday = isThisMonth && today.getDate() === day;
                // Enriched, clickable, search-filtered events for this calendar date.
                const q = search.trim().toLowerCase();
                const dateEvents = [];
                bookings.forEach(b => {
                  (b.trips || []).forEach(t => {
                    if (!t.pickup?.datetime) return;
                    const dt = new Date(t.pickup.datetime);
                    if (dt.getFullYear() === year && dt.getMonth() === month && dt.getDate() === day) {
                      const evt = buildEvent(b, t);
                      if (eventMatches(evt, q)) dateEvents.push(evt);
                    }
                  });
                });
                return (
                  <div
                    key={i}
                    className={`cal-month-cell ${isToday ? 'today' : ''}`}
                    style={{ cursor: 'pointer' }}
                    title="Open this day"
                    onClick={() => goToDay(new Date(year, month, day))}
                  >
                    <span className={`cal-month-day-num ${isToday ? 'today' : ''}`}>{day}</span>
                    {dateEvents.slice(0, 3).map((evt) => {
                      const tone = TONES[evt.status] || TONES.SCHEDULED;
                      return (
                        <button
                          key={evt.id}
                          type="button"
                          className="cal-month-evt"
                          style={{ background: tone.bg, color: tone.text, borderLeftColor: tone.bar, display: 'block', width: '100%', textAlign: 'left', border: 'none', borderLeft: `3px solid ${tone.bar}`, cursor: 'pointer' }}
                          onClick={(e) => { e.stopPropagation(); setSelected(evt); }}
                          title={`${evt.bookingNumber} · ${evt.label}`}
                        >
                          {evt.customerName ? `${evt.customerName} · ` : ''}{evt.label}
                        </button>
                      );
                    })}
                    {dateEvents.length > 3 && <div className="cal-month-more">+{dateEvents.length - 3} more</div>}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Day view */}
      {view === 'day' && (() => {
        const dayDate = new Date(weekStart);
        const dayEvents = [];
        bookings.forEach(b => {
          (b.trips || []).forEach(trip => {
            if (!trip.pickup?.datetime) return;
            const dt = new Date(trip.pickup.datetime);
            if (dt.toDateString() === dayDate.toDateString()) {
              const startHour = dt.getHours() + dt.getMinutes() / 60;
              const duration = trip.distanceMiles ? Math.max(1, Number(trip.distanceMiles) / 30) : 2;
              const evt = { ...buildEvent(b, trip), start: startHour, end: Math.min(24, startHour + duration) };
              if (eventMatches(evt, search.trim().toLowerCase())) dayEvents.push(evt);
            }
          });
        });

        return (
          <div className="cal-grid-wrap">
            <div className="cal-day-view">
              <div className="cal-time-gutter">
                {Array.from({ length: 18 }, (_, i) => i + 6).map(h => (
                  <div key={h} className="cal-hour-label" style={{ top: `${((h - 6) / 18) * 100}%` }}>{fmtTime(h)}</div>
                ))}
              </div>
              <div className="cal-day-col">
                {Array.from({ length: 18 }, (_, i) => i + 6).map(h => (
                  <div key={h} className="cal-hour-line" style={{ top: `${((h - 6) / 18) * 100}%` }} />
                ))}
                {dayEvents.map(evt => {
                  const top = Math.max(0, ((evt.start - 6) / 18) * 100);
                  const height = Math.max(4, ((evt.end - evt.start) / 18) * 100);
                  const tone = TONES[evt.status] || TONES.SCHEDULED;
                  const isSel = selected?.id === evt.id;
                  return (
                    <button key={evt.id} className={`cal-trip-block ${isSel ? 'selected' : ''}`}
                      style={{ top: `${top}%`, height: `${height}%`, background: tone.bg, borderLeftColor: tone.bar, color: tone.text, outlineColor: isSel ? tone.bar : 'transparent', width: '90%', left: '5%' }}
                      onClick={() => setSelected(isSel ? null : evt)}
                      title={`${evt.bookingNumber} · ${evt.label} · ${evt.pax} pax`}>
                      <span className="cal-trip-label">
                        {evt.customerName ? `${evt.customerName} · ` : ''}{evt.label}
                        {evt.linkedTrip && evt.linkedTrip.tripType !== 'PRIVATE' && (
                          <span className="cal-trip-type-tag">{evt.linkedTrip.tripType}</span>
                        )}
                      </span>
                      <span className="cal-trip-time">
                        {fmtTime(evt.start)} · {evt.pax} pax · {evt.driver ? `${evt.driver.firstName} ${evt.driver.lastName}` : 'Unassigned'}
                        {evt.linkedTrip && evt.linkedTrip.tripType !== 'PRIVATE' && ` · ${evt.linkedTrip.bookedSeats}/${evt.linkedTrip.totalCapacity}`}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Week grid */}
      {view === 'week' && <div className="cal-grid-wrap">
        {/* Day headers */}
        <div className="cal-header">
          <div className="cal-time-gutter-head">Time</div>
          {DAYS.map((d, i) => {
            const date = new Date(weekStart);
            date.setDate(date.getDate() + i);
            return (
              <div
                key={d}
                className={`cal-day-head ${i === todayIndex ? 'today' : ''}`}
                style={{ cursor: 'pointer' }}
                title="Open this day"
                onClick={() => goToDay(date)}
              >
                <span className="cal-day-name">{d}</span>
                <span className={`cal-day-num ${i === todayIndex ? 'today' : ''}`}>{date.getDate()}</span>
                <span className="cal-day-count">{eventsByDay[i].length} trips</span>
              </div>
            );
          })}
        </div>

        {/* Time grid body */}
        <div className="cal-body">
          {/* Hour labels */}
          <div className="cal-time-gutter">
            {Array.from({ length: 18 }, (_, i) => i + 6).map(h => (
              <div key={h} className="cal-hour-label" style={{ top: `${((h - 6) / 18) * 100}%` }}>
                {fmtTime(h)}
              </div>
            ))}
          </div>

          {/* Day columns */}
          {DAYS.map((_, dayIdx) => (
            <div key={dayIdx} className={`cal-day-col ${dayIdx === todayIndex ? 'today' : ''}`}>
              {/* Hour lines */}
              {Array.from({ length: 18 }, (_, i) => i + 6).map(h => (
                <div key={h} className="cal-hour-line" style={{ top: `${((h - 6) / 18) * 100}%` }} />
              ))}

              {/* Trip blocks */}
              {eventsByDay[dayIdx].map(evt => {
                const top = Math.max(0, ((evt.start - 6) / 18) * 100);
                const height = Math.max(3, ((evt.end - evt.start) / 18) * 100);
                const tone = TONES[evt.status] || TONES.SCHEDULED;
                const isSel = selected?.id === evt.id;
                return (
                  <button
                    key={evt.id}
                    className={`cal-trip-block ${isSel ? 'selected' : ''}`}
                    style={{
                      top: `${top}%`,
                      height: `${height}%`,
                      background: tone.bg,
                      borderLeftColor: tone.bar,
                      color: tone.text,
                      outlineColor: isSel ? tone.bar : 'transparent',
                    }}
                    onClick={() => setSelected(isSel ? null : evt)}
                    title={`${evt.bookingNumber} · ${evt.customerName || ''} · ${evt.label} · ${evt.pax} pax · ${fmtTime(evt.start)}–${fmtTime(evt.end)}`}
                  >
                    <span className="cal-trip-label">{evt.customerName ? `${evt.customerName} · ` : ''}{evt.label}</span>
                    <span className="cal-trip-time">{fmtTime(evt.start)} · {evt.pax} pax</span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>}

      {/* Detail drawer */}
      {selected && (
        <div className="cal-drawer">
          <div className="cal-drawer-head">
            <div>
              <div className="cal-drawer-title">{selected.label}</div>
              <div className="cal-drawer-sub">{selected.bookingNumber}</div>
            </div>
            <button className="cal-drawer-close" onClick={() => setSelected(null)}>×</button>
          </div>
          <div className="cal-drawer-body">
            {selected.customerName && (
              <div className="cal-drawer-row">
                <span className="cal-drawer-label">Customer</span>
                <span className="cal-drawer-value">{selected.customerName}</span>
              </div>
            )}
            <div className="cal-drawer-row">
              <span className="cal-drawer-label">Status</span>
              <span className="cal-drawer-value">
                <span className="cal-drawer-badge" style={{ background: (TONES[selected.status] || TONES.SCHEDULED).bg, color: (TONES[selected.status] || TONES.SCHEDULED).text }}>
                  {selected.status}
                </span>
              </span>
            </div>
            <div className="cal-drawer-row">
              <span className="cal-drawer-label">Time</span>
              <span className="cal-drawer-value">{fmtTime(selected.start)} – {fmtTime(selected.end)}</span>
            </div>
            <div className="cal-drawer-row">
              <span className="cal-drawer-label">Pickup</span>
              <span className="cal-drawer-value">{shortLoc(selected.trip.pickup)}</span>
            </div>
            <div className="cal-drawer-row">
              <span className="cal-drawer-label">Dropoff</span>
              <span className="cal-drawer-value">{shortLoc(selected.trip.dropoff)}</span>
            </div>
            <div className="cal-drawer-row">
              <span className="cal-drawer-label">Passengers</span>
              <span className="cal-drawer-value">{selected.pax}</span>
            </div>
            <div className="cal-drawer-row">
              <span className="cal-drawer-label">Driver</span>
              <span className="cal-drawer-value">{selected.driver ? `${selected.driver.firstName} ${selected.driver.lastName}` : 'Unassigned'}</span>
            </div>
            {selected.vehicle && (
              <div className="cal-drawer-row">
                <span className="cal-drawer-label">Vehicle</span>
                <span className="cal-drawer-value">{selected.vehicle}</span>
              </div>
            )}
            {selected.trip.estimatedAmount != null && (
              <div className="cal-drawer-row">
                <span className="cal-drawer-label">Fare</span>
                <span className="cal-drawer-value cal-mono">${Number(selected.trip.estimatedAmount).toFixed(2)}</span>
              </div>
            )}
            {!LIVE_OR_DONE.includes(selected.status) && (
              <div style={{ marginTop: 14 }}>
                <button
                  type="button"
                  className="cal-today-btn"
                  style={{ width: '100%' }}
                  onClick={() => setEditEvent(selected)}
                >
                  Edit trip
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {editEvent && (
        <TripEditModal
          row={{
            bookingId: editEvent.bookingId,
            bookingNumber: editEvent.bookingNumber,
            customerId: editEvent.customerId,
            customerName: editEvent.customerName,
            trip: editEvent.trip,
            linkedTrip: editEvent.linkedTrip,
            driverId: editEvent.driverId,
            driver: editEvent.driver,
          }}
          drivers={drivers.filter(d => d.isActive !== false)}
          getHeaders={getHeaders}
          assignDriver={assignDriver}
          unassignDriver={unassignDriver}
          onClose={() => setEditEvent(null)}
          onSaved={() => { setEditEvent(null); setSelected(null); fetchAll(); }}
          toast={toast}
        />
      )}
    </div>
  );
}

export default OpsCalendar;
